<template>
    <div>
        <div class="row m-t-6">
            <aside class="column small-12 medium-3">
                <div class="card m-b-4 p-4 br">
                    <div class="form-group form-group-compact">
                        <label class="sc-reader">{{ trans('Search') }}</label>
                        <div class="form-group-addon">
                            <input class="input__field input-small" type="text" v-bind:placeholder="trans('Search Members')" v-model="selectedQuery" v-on:keydown.enter="filterMembers">
                            <span class="form-append i-container btn btn-text i-filled-dark width-auto-min p-r-0">
                                <!-- /icons/search.svg -->
                                <svg class="icon m-l-1 i-size-small" width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M16.7 15.3c.9-1.2 1.4-2.6 1.4-4.2 0-3.9-3.1-7.1-7-7.1S4 7.2 4 11.1c0 3.9 3.2 7.1 7.1 7.1 1.6 0 3.1-.5 4.2-1.4l3 3c.2.2.5.3.7.3.2 0 .5-.1.7-.3.4-.4.4-1 0-1.4l-3-3.1zm-5.6.8c-2.8 0-5.1-2.2-5.1-5S8.3 6 11.1 6s5.1 2.3 5.1 5.1-2.3 5-5.1 5z" class="icon"/></svg>
                            </span>
                        </div>
                    </div>
                    <div class="form-group form-group-compact">
                        <label class="sc-reader">{{ trans('Filter by') }}</label>
                        <select class="input-small input__field" v-model="selectedFilter">
                            <option v-bind:value="null">{{ trans('Filter by ...') }}</option>
                            <option value="hub">{{ trans('Filter by Hub') }}</option>
                            <option value="membership">{{ trans('Filter by Membership') }}</option>
                        </select>
                    </div>
                    <div v-if="selectedFilter === 'hub'" class="form-group form-group-compact">
                        <label class="sc-reader">{{ trans('Hub') }}</label>
                        <select class="input-small input__field" v-model="selectedHub">
                            <option v-bind:value="null">{{ trans('All Hubs') }}</option>
                            <option v-for="hub in hubs" v-bind:value="hub.encoded_id" v-bind:key="hub.encoded_id">{{ hub.title }}</option>
                        </select>
                    </div>
                    <div v-if="selectedFilter === 'membership'" class="form-group form-group-compact">
                        <label class="sc-reader">{{ trans('Membership') }}</label>
                        <select class="input-small input__field" v-model="selectedMembership">
                            <option v-bind:value="null">{{ trans('All Memberships') }}</option>
                            <option v-for="membership in memberships" v-bind:value="membership.encoded_id" v-bind:key="membership.encoded_id">{{ membership.name }}</option>
                        </select>
                    </div>
                    <button v-on:click="filterMembers" v-bind:class="{'btn-loading': searching}" type="button" class="btn btn-primary btn--block m-b-2">
                        {{ trans('Search') }}
                        <div v-if="searching" class="loader">
                            <div class="chartdonut chartdonut_animation--rotate-tiny chartdonut--tiny">
                                <svg class="i-stroke-bright" width="24" height="24"><g><circle class="icon" cx="50%" cy="50%" r="10" stroke-width="2" fill="none" stroke-dashoffset="5"></circle></g></svg>
                            </div>
                        </div>
                    </button>
                    <div class="text-center">
                        <button v-on:click="resetFilter" type="button" class="btn btn-text btn-text--muted width-auto-min">{{ trans('Reset Filter') }}</button>
                    </div>
                </div>
            </aside>
            <div class="member-content column small-12 medium-9">
                <div class="member-list-group">
                    <div class="members-list-header row row-collapse row-middle br-bottom m-b-5 p-b-4">
                        <div class="column text-left">
                            <span v-if="loading" class="c-medium m-r-5 f-s-3">{{ trans('Loading members...') }}</span>
                            <span v-else class="c-medium m-r-5 f-s-3">{{ trans(':num members', {'num': paginator.total}) }}</span>
                        </div>
                        <div class="column text-right">
                            <a class="btn btn-text btn-text--muted c-muted f-s-3" v-bind:href="exportUrl">{{ trans('Export members') }}</a>
                        </div>
                    </div>
                    <div class="member-list-content">
                        <div v-if="loading" style="margin: 0 auto;" class="chartdonut chartdonut_animation--rotate-large chartdonut--large">
                            <svg id="svgElement" width="53" height="53"><g><circle cx="50%" cy="50%" r="23" stroke-width="3" stroke="#02DCAA" fill="none" stroke-dashoffset="5"></circle></g></svg>
                        </div>
                        <div v-else>
                            <section v-for="(group, letter, index) in letterGroups" v-bind:key="index" class="member-list-group-item row row-collapse m-b-5">
                                <div class="column">
                                    <div v-if="index > 0" class="br-top m-b-5"></div>
                                    <div class="slat">
                                        <div class="member-list-index p-t-3">
                                            <div class="br i-size-large p-2 rounded-xlarge text-center">
                                                <h4 class="m-t-1 p-t-micro f-w-regular">{{ letter }}</h4>
                                            </div>
                                        </div>
                                        <div v-for="member in group" v-bind:key="member.id" class="p-3 rounded-ittybitty row row-middle h-anim h-bg-accent--light">
                                            <div>
                                                <a v-bind:href="memberShowLink(member)">
                                                    <div class="avatar">
                                                        <img v-if="member.image" class="avatar-image avatar-medium" v-bind:src="member.image_url.small">
                                                        <avatar-img v-else class="avatar-image avatar-medium" v-bind:src="getGravatarUrl(member.email)"></avatar-img>
                                                    </div>
                                                </a>
                                            </div>
                                            <div class="slat-content column">
                                                <a v-bind:href="memberShowLink(member)">
                                                    <h4 class="f-s-4 m-b-1 c-primary">{{ member.full_name }} <span class="f-s-2 f-w-regular c-medium-tint">{{ member.email }}</span></h4>
                                                </a>
                                                <p class="f-s-3 m-0 c-medium-tint">{{ trans('Joined') }} {{ memberJoinedDate(member) }}</p>
                                            </div>
                                            <div class="slat-aside text-right">
                                                <a class="i-container i-filled-accent btn btn-text width-auto-min p-0 m-r-0" v-bind:href="memberShowLink(member)">
                                                    <!-- /icons/right.svg -->
                                                    <svg class="icon i-size-small" width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M14.671 12l-6.424 7.341 1.506 1.318L17.329 12 9.753 3.341 8.247 4.66z" class="icon"/></svg>
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <paginator
            v-if=" ! loading"
            v-bind:to="paginator.to"
            v-bind:from="paginator.from"
            v-bind:total="paginator.total"
            v-bind:last-page="paginator.lastPage"
            v-bind:current-page="paginator.currentPage"
            v-on:paginator:switch-page="changePage"
        ></paginator>
    </div>
</template>
<script>
import { memberShowLink } from '../routes';
import { getGravatarUrlFromEmail } from '../helpers';
import Paginator from './Paginator.vue';
import AvatarImg from './AvatarImg.vue';

export default {
    components: {
        Paginator,
        AvatarImg
    },
    props: {
        members: {
            type: Object
        },
        hubs: {
            type: Array
        },
        memberships: {
            type: Array
        },
        hub: {
            type: String,
            default: null,
        },
        query: {
            type: String,
            default: null,
        },
        membership: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            items: _.get(this.members, 'data', []),
            letterGroups: {},
            selectedHub: this.hub,
            selectedMembership: this.membership,
            paginator: {
                to: _.get(this.members, 'to', 1),
                from: _.get(this.members, 'from', 1),
                total: _.get(this.members, 'total', 1),
                lastPage: _.get(this.members, 'last_page', 1),
                currentPage: _.get(this.members, 'current_page', 1),
            },
            loading: false,
            searching: false,
            selectedQuery: this.query,
            selectedFilter: this.membership ? 'membership' : this.hub ? 'hub' : null,
        }
    },
    computed: {
        exportUrl() {
            return window.Kourses.activeBaseUrl + '/members/export'
                + '?hub=' + (this.selectedHub || '')
                + '&membership=' + (this.selectedMembership || '')
                + '&query=' + (this.selectedQuery || '');
        },
    },
    watch: {
        selectedFilter() {
            this.selectedHub = this.selectedMembership = null;
        }
    },
    methods: {
        memberShowLink(member) {
            return memberShowLink(member);
        },
        getGravatarUrl(email) {
            return getGravatarUrlFromEmail(email);
        },
        splitIntoLetterGroups(members) {
            let groups = {};

            for (var member of members) {
                let firstChar = member.last_name.charAt(0).toUpperCase();

                // We need to initialize "letter group" as empty array
                if ( ! _.has(groups, firstChar)) {
                    groups[firstChar] = [];
                }

                groups[firstChar].push(member);
            }

            return groups;
        },
        memberJoinedDate(member) {
            let joinedDate = new Date(member.created_at);

            return joinedDate.toLocaleDateString(window.Kourses.locale, {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
        },
        changePage(page) {
            this.loading = true;

            window.axios.get(Kourses.apiBaseUrl + '/members',{
                params: {
                    'page': page,
                    'query': this.selectedQuery,
                    'hub': this.selectedHub,
                    'membership': this.selectedMembership,
                }
            }).then(this.showMembers);
        },
        filterMembers() {
            this.loading = true;
            this.searching = true;

            window.axios.get(Kourses.apiBaseUrl + '/members',{
                params: {
                    'page': 1,
                    'query': this.selectedQuery,
                    'hub': this.selectedHub,
                    'membership': this.selectedMembership,
                }
            }).then(this.showMembers);
        },
        resetFilter() {
            this.loading = true;
            this.selectedQuery = null;
            this.selectedFilter = null;
            this.selectedHub = null;
            this.selectedMembership = null;

            window.axios.get(Kourses.apiBaseUrl + '/members',{
                params: {
                    'page': 1,
                    'query': this.selectedQuery,
                    'hub': this.selectedHub,
                    'membership': this.selectedMembership
                }
            }).then(this.showMembers);
                window.axios.get(Kourses.apiBaseUrl + '/members',{
                    params: {
                        'page': page,
                        'query': this.selectedQuery,
                        'hub': this.selectedHub,
                        'membership': this.selectedMembership
                    }
                }).then(this.showMembers);
        },
        showMembers(response) {
            this.paginator = {
                to: response.data.to,
                from: response.data.from,
                total: response.data.total,
                lastPage: response.data.last_page,
                currentPage: response.data.current_page
            };
            this.items = response.data.data;
            this.letterGroups = this.splitIntoLetterGroups(this.items);
            this.loading = false;
            this.searching = false;
        }
    },
    created() {
        this.letterGroups = this.splitIntoLetterGroups(this.items);
    },
}
</script>